import { useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | 'system';

function getThemeFromCookie(): Theme {
    if (typeof window === 'undefined') return 'system';
    
    const cookies = document.cookie.split(';');
    const appearanceCookie = cookies.find(c => c.trim().startsWith('appearance='));
    if (appearanceCookie) {
        const value = appearanceCookie.split('=')[1].trim() as Theme;
        return value;
    }
    
    return 'system';
}

export function useTheme() {
    const [theme, setTheme] = useState<Theme>(getThemeFromCookie);
    const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => {
        // Initial resolved theme hesapla
        const initialTheme = getThemeFromCookie();
        if (initialTheme === 'system') {
            return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        return initialTheme === 'dark' ? 'dark' : 'light';
    });

    useEffect(() => {
        const root = window.document.documentElement;
        
        // System tercihini dinle
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        
        const updateTheme = () => {
            let resolved: 'light' | 'dark' = 'light';
            
            if (theme === 'system') {
                resolved = mediaQuery.matches ? 'dark' : 'light';
            } else {
                resolved = theme === 'dark' ? 'dark' : 'light';
            }
            
            setResolvedTheme(resolved);
            
            // HTML class'ını güncelle
            root.classList.remove('light', 'dark');
            root.classList.add(resolved);
            
            // data-theme attribute'unu güncelle (eski kod ile uyumluluk için)
            root.setAttribute('data-theme', resolved);
        };
        
        updateTheme();
        
        // System theme değişikliklerini dinle
        const handler = () => {
            if (theme === 'system') {
                updateTheme();
            }
        };
        
        mediaQuery.addEventListener('change', handler);
        return () => mediaQuery.removeEventListener('change', handler);
    }, [theme]);

    const changeTheme = (newTheme: Theme) => {
        const root = window.document.documentElement;
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        
        // Resolved theme'i hesapla
        let resolved: 'light' | 'dark' = 'light';
        if (newTheme === 'system') {
            resolved = mediaQuery.matches ? 'dark' : 'light';
        } else {
            resolved = newTheme === 'dark' ? 'dark' : 'light';
        }
        
        // DOM'u hemen güncelle (View Transitions API için gerekli)
        root.classList.remove('light', 'dark');
        root.classList.add(resolved);
        root.setAttribute('data-theme', resolved);
        
        // State'i güncelle
        setTheme(newTheme);
        setResolvedTheme(resolved);
        
        // Cookie'yi güncelle (30 gün)
        const expires = new Date();
        expires.setDate(expires.getDate() + 30);
        document.cookie = `appearance=${newTheme}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
    };

    return {
        theme,
        resolvedTheme,
        setTheme: changeTheme,
    };
}

